import { Injectable } from '@angular/core';
import { Observable, Subject, map, distinctUntilChanged } from 'rxjs';
import { skip } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthClient, AuthEvent } from './auth-client';
import { AuthService } from './auth.service';
import { UserProxy } from '../store/user/user.proxy';

@Injectable({ providedIn: 'root' })
export class AuthClientAdapter implements AuthClient {
  private eventsSubject = new Subject<AuthEvent>();

  readonly isAuthenticated$: Observable<boolean> = this.authService.isAuthenticated$;
  readonly user$: Observable<unknown | null> = this.userProxy.userEmail$.pipe(
    map((email) => (email ? { email } : null))
  );
  readonly events$?: Observable<AuthEvent> = this.eventsSubject.asObservable();

  constructor(
    private authService: AuthService,
    private oauthService: OAuthService,
    private userProxy: UserProxy
  ) {
    this.authService.isAuthenticatedSubject$
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((isAuth) => {
        const type = isAuth ? 'login' : 'logout';
        this.eventsSubject.next({ type, at: Date.now() });
      });
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const token = this.oauthService.getAccessToken();
      return token || null;
    } catch {
      return null;
    }
  }

  login(redirectUri?: string): Promise<void> {
    this.authService.login(redirectUri);
    return Promise.resolve();
  }

  logout(options?: { redirectUri?: string }): Promise<void> {
    this.authService.logout();
    return Promise.resolve();
  }

  async refreshToken(): Promise<string | null> {
    try {
      const result: any = await this.authService.tryRefreshToken();
      if (result && typeof result.access_token === 'string') {
        return result.access_token;
      }
      return this.oauthService.getAccessToken() || null;
    } catch {
      return null;
    }
  }
}
